import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '../../../../init-store';
import {
    getBodyThemeName,
    isMessengerIntegration,
    selectIsDebugToolsEnabled,
    selectIsMeetingRoomIntegration,
    selectIsOnPremise,
    selectIsPdokHeaderEnabled,
    selectIsYandexNet,
} from '../config/selectors';
import { selectIsPaasEnabled, selectIsPromozavrPdokEnabled } from '../flags/selectors';
import { isMobileUa } from '../uatraits/selectors';

export const selectPromozavrState = (state: RootState) => state.promozavr;

export const selectShouldUsePaas = createSelector(
    [selectIsPaasEnabled, selectIsOnPremise],
    (isPaasEnabled, isOnPremise) => isPaasEnabled && !isOnPremise
);

export const selectShouldUsePdokPromoRenderer = createSelector(
    [selectShouldUsePaas, selectIsPromozavrPdokEnabled, selectIsPdokHeaderEnabled],
    (shouldUsePaas, isPromozavrPdokEnabled, usePdokHeader) =>
        shouldUsePaas && isPromozavrPdokEnabled && Boolean(usePdokHeader)
);

export const selectIsPromozavrBlockedByEmbed = createSelector(
    [isMessengerIntegration, selectIsMeetingRoomIntegration],
    (isMessenger, isMeetingRoom) => isMessenger || isMeetingRoom
);

const selectPromozavrBodyThemeName = (state: RootState, pathname: string) => getBodyThemeName(state, pathname);

export const selectPromozavrRuntimeContext = createSelector(
    [
        selectShouldUsePaas,
        selectShouldUsePdokPromoRenderer,
        selectIsDebugToolsEnabled,
        selectIsYandexNet,
        isMessengerIntegration,
        selectIsMeetingRoomIntegration,
        isMobileUa,
        selectPromozavrState,
        (state: RootState) => state.config.appLang,
        (state: RootState) => state.config.yaServices.yandex360,
        (state: RootState) => state.config.serverTime,
        selectPromozavrBodyThemeName,
    ],
    (
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isDebugToolsEnabled,
        isYandexNet,
        isMessenger,
        isMeetingRoomIntegration,
        isTouch,
        promozavrState,
        appLang,
        yandex360Host,
        serverTime,
        bodyThemeName
    ) => ({
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isDebugToolsEnabled,
        isYandexNet,
        isMessenger,
        isMeetingRoomIntegration,
        isTouch,
        appLang,
        yandex360Host,
        serverTime,
        bodyThemeName,
        ...promozavrState,
    })
);

export const selectPromozavrStatus = (state: RootState) => selectPromozavrState(state).status;
export const selectPromozavrRequest = (state: RootState) => selectPromozavrState(state).request;
export const selectPromozavrRequestKey = (state: RootState) => selectPromozavrState(state).requestKey;
export const selectPromozavrRendererSource = (state: RootState) => selectPromozavrState(state).rendererSource;
export const selectPromozavrLastError = (state: RootState) => selectPromozavrState(state).lastError;
